/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/** The open message: header, actions, attachments and the sanitised body. */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { Avatar, Box, Chip, CircularProgress, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ForwardIcon from '@mui/icons-material/Forward';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import ReplyAllIcon from '@mui/icons-material/ReplyAll';
import ReplyIcon from '@mui/icons-material/Reply';
import { useAuth } from 'auth/Auth';
import { avatarTone, formatFileSize, formatFullDate, initialsFor, parseAddress } from '../emailFormat';

function downloadBase64(base64Data, filename, mimeType) {
  // Gmail returns URL-safe base64; atob only accepts the standard alphabet.
  const normalised = String(base64Data || '').replace(/-/g, '+').replace(/_/g, '/');
  const byteCharacters = atob(normalised);
  const bytes = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) bytes[i] = byteCharacters.charCodeAt(i);
  const blob = new Blob([bytes], { type: mimeType || 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'attachment');
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function EmailAttachments({ messageId, attachments, onError }) {
  const { axiosInstance } = useAuth();
  const [downloading, setDownloading] = useState({});

  if (!attachments || attachments.length === 0) return null;

  const handleDownload = async (attachment) => {
    setDownloading((prev) => ({ ...prev, [attachment.id]: true }));
    try {
      const response = await axiosInstance.get(
        `/emails/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachment.id)}`
      );
      downloadBase64(response.data.data, attachment.filename, attachment.mimeType);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      onError('Failed to download attachment');
    } finally {
      setDownloading((prev) => ({ ...prev, [attachment.id]: false }));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      {attachments.map((attachment) => (
        <Chip
          key={attachment.id || attachment.filename}
          variant="outlined"
          clickable
          onClick={() => handleDownload(attachment)}
          icon={downloading[attachment.id] ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
          label={
            <Box component="span" sx={{ display: 'inline-flex', gap: 0.75, alignItems: 'baseline', maxWidth: 260 }}>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {attachment.filename}
              </Box>
              <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem', flexShrink: 0 }}>
                {formatFileSize(attachment.size)}
              </Box>
            </Box>
          }
          sx={{ borderRadius: 2, height: 32 }}
        />
      ))}
    </Box>
  );
}

EmailAttachments.propTypes = {
  messageId: PropTypes.string.isRequired,
  attachments: PropTypes.arrayOf(PropTypes.object),
  onError: PropTypes.func.isRequired,
};

EmailAttachments.defaultProps = { attachments: [] };

function ActionButton({ title, onClick, disabled, children }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" onClick={onClick} disabled={disabled} aria-label={title}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

ActionButton.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

ActionButton.defaultProps = { disabled: false };

function AddressLine({ label, value }) {
  if (!value) return null;
  return (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere' }}>
      <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
        {label}
      </Box>
      {value}
    </Typography>
  );
}

AddressLine.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string };
AddressLine.defaultProps = { value: '' };

export function ReaderPlaceholder() {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', p: 3 }}>
      <DraftsOutlinedIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.5 }} />
      <Typography variant="body2" color="text.secondary">
        Select a message to read it here.
      </Typography>
    </Box>
  );
}

function EmailReaderPane({ email, onBack, onReply, onReplyAll, onForward, onMarkUnread, onDelete, busy, onError }) {
  const sender = parseAddress(email.from, email.from_name);
  const tone = avatarTone(sender.email || sender.name);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        {onBack && (
          <ActionButton title="Back to list" onClick={onBack}>
            <ArrowBackIcon fontSize="small" />
          </ActionButton>
        )}
        <ActionButton title="Reply" onClick={onReply}>
          <ReplyIcon fontSize="small" />
        </ActionButton>
        <ActionButton title="Reply all" onClick={onReplyAll}>
          <ReplyAllIcon fontSize="small" />
        </ActionButton>
        <ActionButton title="Forward" onClick={onForward}>
          <ForwardIcon fontSize="small" />
        </ActionButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ActionButton title="Mark as unread" onClick={onMarkUnread} disabled={busy}>
          <MarkEmailUnreadOutlinedIcon fontSize="small" />
        </ActionButton>
        <ActionButton title="Delete" onClick={onDelete} disabled={busy}>
          <DeleteOutlineIcon fontSize="small" />
        </ActionButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2.5, minHeight: 0 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2, overflowWrap: 'anywhere' }}>
          {email.subject || '(no subject)'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ width: 40, height: 40, fontSize: '0.875rem', fontWeight: 600, bgcolor: tone.background, color: tone.text }}>
            {initialsFor(sender.name || sender.email)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" component="span" sx={{ overflowWrap: 'anywhere' }}>
                {sender.name || sender.email}
              </Typography>
              {sender.name && sender.email && sender.name !== sender.email && (
                <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  {`<${sender.email}>`}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>
                {formatFullDate(email.date)}
              </Typography>
            </Box>
            <AddressLine label="To" value={email.to} />
            <AddressLine label="Cc" value={email.cc} />
            <AddressLine label="Bcc" value={email.bcc} />
          </Box>
        </Box>

        <EmailAttachments messageId={email.id} attachments={email.attachments} onError={onError} />

        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            maxWidth: '100%',
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            color: 'text.primary',
            '& img': { maxWidth: '100%', height: 'auto' },
            '& a': { color: 'primary.main', overflowWrap: 'anywhere', wordBreak: 'break-word' },
            '& pre, & code': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
            '& table': { maxWidth: '100%' },
          }}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(email.body || '', { USE_PROFILES: { html: true } }),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

EmailReaderPane.propTypes = {
  email: PropTypes.object.isRequired,
  onBack: PropTypes.func,
  onReply: PropTypes.func.isRequired,
  onReplyAll: PropTypes.func.isRequired,
  onForward: PropTypes.func.isRequired,
  onMarkUnread: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  onError: PropTypes.func.isRequired,
};

EmailReaderPane.defaultProps = { onBack: null, busy: false };

export default EmailReaderPane;
